import type { SiteOrder } from '@api/vendor-portal';

import { openRemaining } from '@components/containers/vendorHome/orderTotals';
import { groupLinesBy } from '@utils/rowTree';

const CANCELLED = 'CANCELLED';

/**
 * One shape for both levels of the site's Production grid.
 *
 * DataSheet flattens expanded children into the same virtualized list, so a child must be the
 * same row type as its parent — every column renderer then works unchanged at both levels. Same
 * constraint, and the same answer, as the brand's Production Orders grid.
 *
 * DELIBERATELY NOT SHARED WITH THAT ONE. The two grids carry different columns — the brand's has
 * the manufacturer, who it was sent to and its document; this has in-production and manufactured
 * — so a common row type would be a union with half its fields null on each side. The pattern is
 * worth copying; the type is not.
 */
export interface SiteProductionRow {
    key: string;
    kind: 'order' | 'line';
    /** The order's code on an order row, the work order's on a line. */
    orderCode: string;
    /** Blank on an order row: its own code is already in the first column. */
    code: string;
    /** The SKU on a line; on an order, how many of them there are. */
    productName: string;
    /**
     * WHICH SKU, as opposed to what it is called.
     *
     * Carried because grouping by product has to key on it: two SKUs can share a name — a
     * renamed product, a colourway entered twice — and folding on what is printed would merge
     * two commitments into one row whose figures belong to neither. Empty on an order row,
     * which is not about one product.
     */
    productId: string;
    ordered: number;
    produced: number;
    /**
     * Where the goods are NOW: cleared and still here, and declared but unruled.
     *
     * Neither is cumulative — a batch that passed and then shipped has left `qcApproved` for
     * `dispatched`, so the columns never double-count the same unit.
     */
    qcApproved: number;
    qcPending: number;
    dispatched: number;
    /** Ordered minus made, on a live line only — see openRemaining. Floored per line. */
    openQuantity: number;
    dueDate: string | null;
    /**
     * When the order was placed, and what it is worth.
     *
     * `total` IS THE ORDER'S ON AN ORDER ROW AND THE LINE'S OWN ON A LINE — the SKU's quantity
     * by its rate, tax in, as the order document prints it. Never the order's total repeated
     * under each SKU, which read as each line being worth the whole order. Blank on a cancelled
     * line, and on a line that was never priced. `orderDate` sits on the order row, and on its
     * lines once they are filed under a product and the order row is gone.
     */
    orderDate: string | null;
    total: string | null;
    currency: string | null;
    /**
     * THE ORDER'S status on an order row, THE LINE'S on a line row.
     *
     * Worth having only because the order's status became truthful: until it was derived from
     * its lines it read "in production" for every order ever raised, and a header saying that
     * above three shipped lines would have been worse than no header at all.
     */
    status: string;
    /** The ids a cancel needs: the order's on an order row, the work order's on a line. */
    productionOrderId: string | null;
    workOrderId: string | null;
    /**
     * Text the grid's search should match on but no column shows — the order codes gathered
     * under a product, when the sheet is grouped by SKU.
     *
     * DataSheet searches the values of a TOP-LEVEL row and only walks into children once they
     * are expanded, so anything that moved a level down stops being findable. This puts it back
     * without spending a column on it.
     */
    searchBlob?: string;
    lines?: SiteProductionRow[];
}

/**
 * What a parent row says about the lines beneath it — an order's SKUs, or a product's orders.
 *
 * A CANCELLED LINE IS A RECORD, NOT A COMMITMENT. It stays visible under its parent with its
 * own figures and its pill, and adds nothing to the parent: not to what was ordered, made, is
 * standing here or has gone, not to what is still owed, not to the value, and not to the count
 * of lines. Everywhere else in the app a cancelled line is dropped at the query; this is the
 * one screen that keeps it on view, and "shown" and "counted" are different things.
 *
 * ONE UNPRICED LINE BLANKS THE VALUE: a partial sum sits in the column looking exactly like a
 * total, and is the one number on the row that would be wrong without looking wrong.
 */
const rollUp = (lines: SiteProductionRow[]) => {
    const counted = lines.filter((line) => line.status !== CANCELLED);
    const sum = (pick: (line: SiteProductionRow) => number) =>
        counted.reduce((total, line) => total + (pick(line) || 0), 0);

    return {
        count: counted.length,
        ordered: sum((line) => line.ordered),
        produced: sum((line) => line.produced),
        qcApproved: sum((line) => line.qcApproved),
        qcPending: sum((line) => line.qcPending),
        dispatched: sum((line) => line.dispatched),
        // Already floored per line; summing the floored figures is what stops an over-made SKU
        // on one line cancelling a shortfall on another.
        openQuantity: sum((line) => line.openQuantity),
        total:
            counted.length === 0 || counted.some((line) => line.total === null)
                ? null
                : sum((line) => Number(line.total)).toFixed(2),
    };
};

const label = (count: number, noun: string) => `${count} ${noun}${count === 1 ? '' : 's'}`;

/**
 * The order-grained tree the grid renders.
 *
 * A HEADERLESS WORK ORDER STAYS FLAT. A line raised without a production order arrives carrying
 * its own code as the order's, and nesting it would put a disclosure triangle in front of a
 * group of one — an invitation to expand something with nothing inside it.
 *
 * Order preserved as the server sent it, which is soonest due first.
 */
export function buildSiteProductionRows(orders: SiteOrder[]): SiteProductionRow[] {
    return orders.map((order) => {
        const lines = order.lines ?? [];

        const children: SiteProductionRow[] = lines.map((line) => ({
            // Prefixed so a parent and a child can never collide on a key, which DataSheet
            // requires and which is easy to get wrong when both carry codes.
            key: `line:${line.workOrderId}`,
            kind: 'line' as const,
            orderCode: '',
            code: line.code,
            productName: line.productName ?? line.productId,
            productId: line.productId,
            ordered: line.ordered ?? 0,
            produced: line.produced ?? 0,
            qcApproved: line.qcApproved ?? 0,
            qcPending: line.qcPending ?? 0,
            dispatched: line.dispatched ?? 0,
            openQuantity: openRemaining(line.status, line.ordered ?? 0, line.produced ?? 0),
            dueDate: line.dueDate,
            orderDate: null,
            // A cancelled line is worth nothing to anyone, whatever rate it was raised at.
            total: line.status === CANCELLED ? null : (line.lineValue ?? null),
            currency: order.currency,
            status: line.status,
            productionOrderId: null,
            workOrderId: line.workOrderId,
        }));

        const headerless = !order.productionOrderId;
        if (headerless) {
            const [only] = children;
            return only
                ? { ...only, key: `wo:${order.code}`, orderCode: order.code }
                : {
                      key: `wo:${order.code}`,
                      kind: 'line' as const,
                      orderCode: order.code,
                      code: order.code,
                      productName: '',
                      productId: '',
                      ordered: 0,
                      produced: 0,
                      qcApproved: 0,
                      qcPending: 0,
                      dispatched: 0,
                      openQuantity: 0,
                      dueDate: null,
                      orderDate: null,
                      total: null,
                      currency: null,
                      status: order.status,
                      productionOrderId: null,
                      workOrderId: null,
                  };
        }

        const { count, total: _lineValues, ...figures } = rollUp(children);

        return {
            key: `order:${order.productionOrderId}`,
            kind: 'order' as const,
            orderCode: order.code,
            code: '',
            productName: label(count, 'SKU'),
            productId: '',
            ...figures,
            dueDate: order.expectedCompletionDate,
            orderDate: order.orderDate,
            // The header's own figure, charges and all — re-priced from its live lines whenever
            // one is cancelled, so it and the lines beneath it agree.
            total: order.total,
            currency: order.currency,
            status: order.status,
            productionOrderId: order.productionOrderId,
            workOrderId: null,
            lines: children,
        };
    });
}

/**
 * The tree narrowed to a set of line statuses, orders with nothing left dropping out.
 *
 * FILTERS THE LINES, NOT THE ORDERS, so picking "Shipped" answers "which orders have shipped
 * lines, and which ones" rather than "which orders are wholly shipped". The order row's figures
 * are re-summed from what survives, or a header would report totals its own children contradict.
 *
 * A SET RATHER THAN ONE STATUS, because the questions asked of this tab are unions: "what is
 * still moving" is issued and in-production together, and asking it one status at a time is two
 * passes over a list whose whole point is to be read once. An empty set is every status.
 */
export function filterSiteProductionRows(
    rows: SiteProductionRow[],
    statuses: string[],
): SiteProductionRow[] {
    if (statuses.length === 0) return rows;

    return rows
        .map((row) => {
            if (row.kind === 'line') return statuses.includes(row.status) ? row : null;

            const kept = (row.lines ?? []).filter((line) =>
                statuses.includes(line.status),
            );
            if (kept.length === 0) return null;

            const { count, total: _lineValues, ...figures } = rollUp(kept);

            return { ...row, productName: label(count, 'SKU'), ...figures, lines: kept };
        })
        .filter((row): row is SiteProductionRow => row !== null);
}

/**
 * The same rows, filed under their product instead of their order.
 *
 * A REARRANGEMENT, NOT A SECOND BUILD — the children are the very line rows the order view
 * renders, so the two groupings cannot disagree about a figure. The vendor's own Production
 * Orders page does the same thing to the same list; the two sides of this portal are looking at
 * one book, and a grouping that existed on only one of them would be a difference to explain.
 *
 * APPLY THE STATUS FILTER FIRST, then this. `filterSiteProductionRows` narrows LINES and re-sums
 * the header above them, and it rewrites `productName` to a SKU count — which is the order
 * count in this grouping. Folding what has already survived the filter keeps one rule in one
 * place instead of teaching the filter about two shapes.
 *
 * `orderDate` AND `status` GO BLANK ON THE PARENT, because neither is true of a product wanted
 * by two orders: it was not placed on one date and has no single status. The date moves down
 * onto the lines, which are now the only rows showing an order. `total` is the product's lines
 * added up — each line's own value, so the sum is a real figure rather than two order totals
 * glued together. `dueDate` becomes the earliest, which is what decides what gets made next.
 */
export function toSkuSiteProductionRows(rows: SiteProductionRow[]): SiteProductionRow[] {
    return groupLinesBy(
        rows,
        (line) => line.productId,
        // The order's code and date move down onto the line, which is now the only row showing
        // one. Not its id — the cancel action reads `productionOrderId` to decide it is looking
        // at an order, so a line must not inherit it.
        (line, order) => ({ ...line, orderCode: order.orderCode, orderDate: order.orderDate }),
    ).map(({ key, lines }) => {
        const [first] = lines;
        const dueDates = lines
            .map((line) => line.dueDate)
            .filter((date): date is string => !!date);
        const { count, ...figures } = rollUp(lines);

        return {
            key: `sku:${key}`,
            kind: 'order' as const,
            /*
             * The product takes the Order column and the codes move under it — the column
             * renames to "Product / Order" in this mode.
             */
            orderCode: first?.productName ?? key,
            code: '',
            productName: label(count, 'order'),
            productId: key,
            ...figures,
            dueDate: dueDates.length > 0 ? dueDates.reduce((a, b) => (a < b ? a : b)) : null,
            orderDate: null,
            currency: figures.total === null ? null : (first?.currency ?? null),
            status: '',
            /*
             * NEITHER ID, so no cancel button appears on a product row. Cancelling is an act
             * against one order or one work order; "cancel this SKU everywhere" is not a thing
             * the brand means, and `resolveCancelAction` reads these to decide what it is
             * looking at.
             */
            productionOrderId: null,
            workOrderId: null,
            /** The order codes, so search still finds them — see `searchBlob`. */
            searchBlob: lines.map((line) => line.orderCode).join(' '),
            lines,
        };
    });
}
